/**
 * Surface the unread count where other sites do: a "(n) " prefix on the
 * tab title, a red dot on the favicon, and the OS app badge when the app is
 * installed (Badging API). Everything is best-effort and restores itself
 * when the count drops to zero. The favicon itself is painted by the shared
 * tab-favicon compositor, so a conversation status disc can take precedence.
 */

import { setUnreadCount } from "src/helpers/tabFavicon";

const TITLE_PREFIX = /^\(\d+\+?\) /;

export const formatTitle = (title: string, unread: number): string => {
  const bare = title.replace(TITLE_PREFIX, "");
  if (unread <= 0) return bare;
  return `(${unread > 99 ? "99+" : unread}) ${bare}`;
};

export function applyTitleBadge(unread: number): void {
  const next = formatTitle(document.title, unread);
  if (next !== document.title) document.title = next;
}

export function applyFaviconBadge(unread: number): void {
  setUnreadCount(unread);
}

export function applyAppBadge(unread: number): void {
  const nav = navigator as Navigator & {
    setAppBadge?: (n?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  const swallow = () => {
    // Not installed, cross-origin frame, or Safari without permission: the
    // API rejects rather than throws, so a sync try/catch would miss it.
  };
  try {
    const result =
      unread > 0 ? nav.setAppBadge?.(unread) : nav.clearAppBadge?.();
    result?.catch(swallow);
  } catch {
    swallow();
  }
}

export function applyTabBadge(unread: number): void {
  applyTitleBadge(unread);
  applyFaviconBadge(unread);
  applyAppBadge(unread);
}
